import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { X, UserPlus, Settings } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

interface SavedAssignee {
  id: string;
  assignee_name: string;
  email: string | null;
  role: string | null;
  usage_count: number;
}

interface AssigneeAutocompleteProps {
  selectedAssignees: string[];
  onAssigneesChange: (assignees: string[]) => void;
  unaId?: string;
  onManageClick: () => void;
}

export const AssigneeAutocomplete = ({
  selectedAssignees,
  onAssigneesChange,
  unaId,
  onManageClick,
}: AssigneeAutocompleteProps) => {
  const { user } = useAuth();
  const [inputValue, setInputValue] = useState("");
  const [savedAssignees, setSavedAssignees] = useState<SavedAssignee[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredAssignees, setFilteredAssignees] = useState<SavedAssignee[]>([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newAssignee, setNewAssignee] = useState({ name: "", email: "", role: "" });
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user) {
      fetchSavedAssignees();
    }
  }, [user, unaId]);

  useEffect(() => {
    const filtered = savedAssignees
      .filter(
        (assignee) =>
          assignee.assignee_name.toLowerCase().includes(inputValue.toLowerCase()) &&
          !selectedAssignees.includes(assignee.assignee_name)
      )
      .sort((a, b) => b.usage_count - a.usage_count)
      .slice(0, 10);

    setFilteredAssignees(filtered);
  }, [inputValue, savedAssignees, selectedAssignees]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchSavedAssignees = async () => {
    try {
      const query = supabase
        .from("saved_assignees")
        .select("*")
        .eq("user_id", user!.id)
        .order("usage_count", { ascending: false });

      if (unaId) {
        query.eq("una_id", unaId);
      }

      const { data, error } = await query;
      if (error) throw error;
      setSavedAssignees(data || []);
    } catch (error) {
      console.error("Error fetching assignees:", error);
    }
  };

  const saveNewAssignee = async (name: string, email?: string, role?: string) => {
    try {
      const { error } = await supabase.from("saved_assignees").insert({
        user_id: user!.id,
        una_id: unaId || null,
        assignee_name: name,
        email: email || null,
        role: role || null,
        usage_count: 1,
      });

      if (error && error.code !== "23505") {
        throw error;
      }

      if (error?.code === "23505") {
        const { data: existing } = await supabase
          .from("saved_assignees")
          .select("usage_count")
          .eq("user_id", user!.id)
          .eq("assignee_name", name)
          .single();
        
        if (existing) {
          await supabase
            .from("saved_assignees")
            .update({ usage_count: existing.usage_count + 1 })
            .eq("user_id", user!.id)
            .eq("assignee_name", name);
        }
      }

      fetchSavedAssignees();
    } catch (error) {
      console.error("Error saving assignee:", error);
    }
  };

  const handleSelectAssignee = async (assigneeName: string) => {
    if (!selectedAssignees.includes(assigneeName)) {
      onAssigneesChange([...selectedAssignees, assigneeName]);

      const { data: existing } = await supabase
        .from("saved_assignees")
        .select("usage_count")
        .eq("user_id", user!.id)
        .eq("assignee_name", assigneeName)
        .single();
      
      if (existing) {
        await supabase
          .from("saved_assignees")
          .update({ usage_count: existing.usage_count + 1 })
          .eq("user_id", user!.id)
          .eq("assignee_name", assigneeName);
      }

      setInputValue("");
      setShowDropdown(false);
    }
  };

  const handleAddNewAssignee = () => {
    const trimmedName = inputValue.trim();
    if (!trimmedName) return;

    if (trimmedName.length > 100) {
      toast.error("Name must be less than 100 characters");
      return;
    }

    setNewAssignee({ name: trimmedName, email: "", role: "" });
    setShowAddDialog(true);
  };

  const handleSaveNewAssignee = async () => {
    if (!newAssignee.name.trim()) {
      toast.error("Name is required");
      return;
    }

    if (selectedAssignees.includes(newAssignee.name)) {
      toast.error("Assignee already added");
      return;
    }

    onAssigneesChange([...selectedAssignees, newAssignee.name]);
    await saveNewAssignee(newAssignee.name, newAssignee.email, newAssignee.role);
    
    setShowAddDialog(false);
    setNewAssignee({ name: "", email: "", role: "" });
    setInputValue("");
    setShowDropdown(false);
  };

  const handleRemoveAssignee = (assigneeToRemove: string) => {
    onAssigneesChange(selectedAssignees.filter((a) => a !== assigneeToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (filteredAssignees.length > 0) {
        handleSelectAssignee(filteredAssignees[0].assignee_name);
      } else if (inputValue.trim()) {
        handleAddNewAssignee();
      }
    } else if (e.key === "Escape") {
      setShowDropdown(false);
    }
  };

  const getInitials = (name: string) => {
    // Safety check: ensure name is a valid string
    if (!name || typeof name !== 'string') {
      return '?';
    }
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (name: string) => {
    const colors = [
      "bg-blue-500",
      "bg-green-500",
      "bg-purple-500",
      "bg-orange-500",
      "bg-pink-500",
      "bg-teal-500",
    ];
    // Safety check: ensure name is a valid string
    if (!name || typeof name !== 'string') {
      return colors[0]; // Return default color
    }
    const hash = name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">👤 Assign To (Optional)</label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onManageClick}
          className="h-8 text-xs"
        >
          <Settings className="h-3 w-3 mr-1" />
          Manage Assignees
        </Button>
      </div>

      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search or add assignee..."
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          onKeyDown={handleKeyDown}
          className="pr-10"
        />
        <UserPlus className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />

        {showDropdown && (inputValue || filteredAssignees.length > 0) && (
          <div
            ref={dropdownRef}
            className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-[300px] overflow-auto"
          >
            {filteredAssignees.length > 0 && (
              <div className="p-2">
                <div className="text-xs font-medium text-muted-foreground px-2 py-1">
                  Saved Assignees:
                </div>
                {filteredAssignees.map((assignee) => (
                  <button
                    key={assignee.id}
                    type="button"
                    onClick={() => handleSelectAssignee(assignee.assignee_name)}
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-accent rounded-md"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className={getAvatarColor(assignee.assignee_name)}>
                          {getInitials(assignee.assignee_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="text-left">
                        <div className="text-sm font-medium">{assignee.assignee_name}</div>
                        {assignee.role && (
                          <div className="text-xs text-muted-foreground">{assignee.role}</div>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {assignee.usage_count} tasks
                    </span>
                  </button>
                ))}
              </div>
            )}

            {inputValue.trim() &&
              !savedAssignees.find((a) => a.assignee_name === inputValue.trim()) && (
                <>
                  {filteredAssignees.length > 0 && <div className="border-t border-border" />}
                  <button
                    type="button"
                    onClick={handleAddNewAssignee}
                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-accent text-sm text-primary"
                  >
                    <span>+ Add "{inputValue.trim()}" as new assignee</span>
                  </button>
                </>
              )}
          </div>
        )}
      </div>

      {selectedAssignees.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedAssignees.map((assignee) => {
            // Handle both string (legacy) and object (new) assignee formats
            const assigneeName = typeof assignee === 'string' ? assignee : (assignee as any).full_name || String(assignee);
            const assigneeKey = typeof assignee === 'string' ? assignee : (assignee as any).id || assigneeName;
            
            return (
              <Badge key={assigneeKey} variant="secondary" className="gap-2 pl-2 pr-1 py-1">
                <Avatar className="h-5 w-5">
                  <AvatarFallback className={`${getAvatarColor(assigneeName)} text-xs`}>
                    {getInitials(assigneeName)}
                  </AvatarFallback>
                </Avatar>
                {assigneeName}
                <button
                  type="button"
                  onClick={() => handleRemoveAssignee(assignee)}
                  className="ml-1 hover:bg-background/50 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Assignee</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={newAssignee.name}
                onChange={(e) => setNewAssignee({ ...newAssignee, name: e.target.value })}
                placeholder="John Doe"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={newAssignee.email}
                onChange={(e) => setNewAssignee({ ...newAssignee, email: e.target.value })}
                placeholder="john@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Input
                id="role"
                value={newAssignee.role}
                onChange={(e) => setNewAssignee({ ...newAssignee, role: e.target.value })}
                placeholder="e.g., Board Member, Advisor"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveNewAssignee}>Add Assignee</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
